import { Announcement01, BarChart10, CalendarDate, CalendarPlus01, FileSearch02, ReceiptCheck, Users01 } from '@opendate-app/setlist';

// Every prompt here maps to a shipped Conductor capability — when adding one,
// make sure a tool can actually answer it (suggesting the impossible creates
// the trust failures the nightly review exists to catch). Fee-breakdown
// prompts are intentionally absent (product decision, 2026-07-01).
// Bracketed [placeholders] are auto-selected in the composer for editing.

export function buildPromptCatalog(context = {}) {
  const venue = context.venueName || '[venue]';
  const upcoming = context.upcomingEventName || '[event]';
  const past = context.pastEventName || '[event]';

  return [
    {
      id: 'calendar',
      label: 'Calendar & holds',
      prompts: [
        `Is [date] open at ${venue}?`,
        `What holds do I have on [date] at ${venue}?`,
        `Find holds at ${venue} that haven't been touched in 60+ days`,
        'Which shows this month still aren’t on sale?',
        'What’s on the calendar for [month]?'
      ]
    },
    {
      id: 'sales',
      label: 'Ticket sales & attendance',
      prompts: [
        `How are ticket sales pacing for ${upcoming} compared to past shows?`,
        `Where did ticket sales for ${upcoming} come from — email, social, direct?`,
        `How many carts were abandoned for ${upcoming} this week?`,
        `What was the no-show rate for ${past}?`,
        'Which shows sold tickets on StubHub this year?',
        'Rank this year’s shows by revenue by genre',
        'How many tickets did we sell today across all shows?'
      ]
    },
    {
      id: 'memberships',
      label: 'Memberships',
      prompts: [
        'How are membership sales this month?',
        'What is our current active-only membership MRR by tier?',
        'How many active and trialing memberships do we have?',
        'How many membership records were created, and how many cancellations were recorded this month?',
        'Break down members and membership promo use by product',
        'How many paid admission and add-on tickets were purchased with membership promos?'
      ]
    },
    {
      id: 'marketing',
      label: 'Marketing & broadcasts',
      prompts: [
        `Draft a broadcast announcing ${upcoming} with a ticket button`,
        'Clone last week’s newsletter for this week',
        'How did our last broadcast perform — opens, clicks, ticket conversions?',
        `Export the buyer list for ${upcoming} for our street team`,
        'What are my top fan markets?',
        `Which cities did ${past} buyers come from?`
      ]
    },
    {
      id: 'booking',
      label: 'Offers & booking',
      prompts: [
        `Build an offer for [artist] at ${venue} using my [template] template`,
        'What offer templates do I have?',
        'Compare this [artist] offer to similar deals we’ve done',
        'Which offers are still waiting on a response?',
        'How many shows has [talent buyer] booked this year?'
      ]
    },
    {
      id: 'finances',
      label: 'Finances & settlements',
      prompts: [
        `Pull the settlement report for ${past}`,
        'Compare ticket revenue this year to last year',
        'What did we make on food & beverage last month?'
      ]
    },
    {
      id: 'actions',
      label: 'Do it for me',
      prompts: [
        `Place an H1 hold for [artist] at ${venue} on [date]`,
        `Clear the [artist] hold at ${venue}`,
        `Duplicate ${past} onto [date]`,
        `Raise the GA price on ${upcoming} to $[price]`,
        'Link my [artist] offer to its confirmed event'
      ]
    },
    {
      id: 'howto',
      label: 'How-to & setup',
      prompts: [
        'How do I set up dynamic pricing?',
        'Connect Opendate to Claude or ChatGPT',
        'How do refunds work?'
      ]
    }
  ];
}

export const EMPTY_STATE_PROMPT_ICONS = [
  CalendarDate,
  FileSearch02,
  BarChart10,
  BarChart10,
  Announcement01,
  CalendarPlus01,
  ReceiptCheck,
  Users01
];

// Curated subset for the empty-state chips — leads with the newest capabilities.
export function buildEmptyStatePrompts(context = {}) {
  const venue = context.venueName || '[venue]';
  const upcoming = context.upcomingEventName || '[event]';
  const past = context.pastEventName || '[event]';

  return [
    `Is [date] open at ${venue}?`,
    `Find holds at ${venue} that haven't been touched in 60+ days`,
    `How are ticket sales pacing for ${upcoming} compared to past shows?`,
    `Where did ticket sales for ${upcoming} come from — email, social, direct?`,
    'Clone last week’s newsletter for this week',
    `Place an H1 hold for [artist] at ${venue} on [date]`,
    `Pull the settlement report for ${past}`,
    'What are my top fan markets?'
  ];
}

const TOOL_PROMPT_GROUPS = {
  check_availability: 'calendar',
  create_hold: 'calendar',
  clear_hold: 'calendar',
  convert_hold: 'calendar',
  venue_events: 'calendar',
  venue_ticket_sales: 'sales',
  venue_order_lookup: 'sales',
  passes_and_packs: 'sales',
  create_pass_collection: 'calendar',
  add_collection_shows: 'calendar',
  series_overview: 'calendar',
  add_series_dates: 'calendar',
  create_pass_product: 'sales',
  set_up_pass_redemption: 'sales',
  classes: 'sales',
  class_attendees: 'sales',
  comp_class_registrations: 'sales',
  transfer_class_registration: 'sales',
  move_class_registration: 'sales',
  offer_class_waitlist_spot: 'sales',
  remove_class_waitlist_entry: 'sales',
  cancel_class_registration: 'sales',
  edit_ticket_prices: 'sales',
  update_manual_ticket_counts: 'sales',
  add_to_guest_list: 'sales',
  edit_show_details: 'calendar',
  save_event_note: 'calendar',
  create_ticket_types: 'sales',
  create_promo_code: 'sales',
  venue_broadcast_engagement: 'marketing',
  create_broadcast_draft: 'marketing',
  create_sms_broadcast_draft: 'marketing',
  list_broadcast_drafts: 'marketing',
  venue_fan_analytics: 'marketing',
  marketing_impact: 'marketing',
  export_event_buyers: 'marketing',
  venue_offers: 'booking',
  create_offer: 'booking',
  list_templates: 'booking',
  link_offer_to_event: 'booking',
  venue_offer_expenses: 'booking',
  venue_financials: 'finances',
  gift_card_lookup: 'finances',
  gift_up_card_lookup: 'finances',
  gift_card_liability: 'finances',
  gift_card_general_ledger: 'finances',
  grants: 'finances',
  deal_pipeline: 'finances',
  venue_settlement_report: 'finances',
  venue_food_and_beverage: 'finances',
  help_docs: 'howto'
};

export const promptGroupForTool = (toolName) => TOOL_PROMPT_GROUPS[toolName] || null;
